import { jsPDF } from 'jspdf';
import autoTable, { type CellHook, type RowInput } from 'jspdf-autotable';
import HuttonsWhiteLogo from '../../img/Huttons_white.png';
import { savePdfDocument } from './savePdfDocument';
import type {
  DecouplingGuard,
  DecouplingPropertyTab,
  ExitingOwnerResult,
  FinancingResult,
  TransferCostResult,
} from './decouplingCalculator';

// ── Colour palette (matches buyingCalculatorPdfExport.ts / sellingCalculatorPdfExport.ts) ──
const ORANGE_RGB: [number, number, number] = [255, 152, 0];
const DARK_BG_RGB: [number, number, number] = [15, 23, 42];
const FOOTER_BG_RGB: [number, number, number] = [20, 30, 48];
const INFO_BOX_BG_RGB: [number, number, number] = [30, 41, 59];

const SUMMARY_BAR_NAVY_RGB: [number, number, number] = [15, 27, 58];
const ALERT_RED_RGB: [number, number, number] = [184, 62, 45];
const SUCCESS_GREEN_RGB: [number, number, number] = [22, 101, 52];

// ── Huttons brand palette (poster page — matches the buying / selling posters) ──
const NAVY: [number, number, number] = [0, 31, 67];        // #001F43
const ORANGE: [number, number, number] = [245, 130, 32];   // #F58220
const GOLD: [number, number, number] = [201, 164, 92];     // #C9A45C
const GREY: [number, number, number] = [120, 122, 128];
const LIGHT_LINE: [number, number, number] = [228, 230, 235];
const CARD_HEADER: [number, number, number] = [239, 242, 248];
const PEACH: [number, number, number] = [253, 240, 232];   // orange tint fill

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AgentInfo {
  Name?: string;
  CEANumber?: string;
  Mobile?: string;
  Email?: string;
  Photo?: string;
}

/** The headline facts of the transfer, pre-formatted by the page. */
export interface DecouplingPdfState {
  tab: DecouplingPropertyTab;
  marketValue: number;
  outstandingLoan: number;
  /** Whether Seller's Stamp Duty was switched on. Off hides the purchase-date row,
   *  which only exists to date the holding period. */
  ssdApplies: boolean;
  /** ISO yyyy-mm-dd, or '' when not set. */
  purchaseDate: string;
  transferDate: string;
  exitingName: string;
  remainingName: string;
  exitingSharePct: number;
  remainingSharePct: number;
  /** The bank's contracted rate, and the MAS floor TDSR was tested at. */
  bankRate: number;
  stressTestRate: number;
}

export interface DecouplingPdfData {
  state: DecouplingPdfState;
  costs: TransferCostResult;
  financing: FinancingResult;
  exitPosition: ExitingOwnerResult;
  guards: DecouplingGuard[];
  agentInfo?: AgentInfo;
  /** Client/group loaded in the Profile Manager — the cover's "prepared for" line. */
  profileName?: string;
}

// ── Formatting ────────────────────────────────────────────────────────────────

// jsPDF's built-in Helvetica is WinAnsi, so every string written to the document
// stays ASCII — plain hyphens, no typographic dashes.
const formatCurrency = (value: number): string =>
  `$${Math.round(value).toLocaleString('en-SG', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const signedCurrency = (value: number): string =>
  (value < 0 ? `-${formatCurrency(Math.abs(value))}` : formatCurrency(value));

const displayDate = (iso: string): string => {
  if (!iso) return '-';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-SG', { day: '2-digit', month: 'short', year: 'numeric' });
};

/** "3 yrs 2 mths" between two ISO dates, for the SSD holding period. '' when unknown. */
const holdingPeriodLabel = (fromIso: string, toIso: string): string => {
  if (!fromIso || !toIso) return '';
  const from = new Date(`${fromIso}T00:00:00`);
  const to = new Date(`${toIso}T00:00:00`);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return '';
  let months = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
  if (to.getDate() < from.getDate()) months -= 1;
  if (months < 0) return '';
  const years = Math.floor(months / 12);
  const rest = months % 12;
  const parts: string[] = [];
  if (years > 0) parts.push(`${years} yr${years > 1 ? 's' : ''}`);
  if (rest > 0) parts.push(`${rest} mth${rest > 1 ? 's' : ''}`);
  return parts.join(' ') || 'under a month';
};

/** Names ride inside table labels, where a long one would wrap the column. */
const shortName = (name: string): string =>
  (name.length > 14 ? `${name.slice(0, 13).trim()}.` : name);

const TAB_LABEL: Record<DecouplingPropertyTab, string> = {
  private: 'Private Property',
  ec: 'Executive Condominium',
  hdb: 'HDB Flat',
};

// Lowercase roman numeral for the milestone badges (i, ii, iii …), as on the
// buying and selling timelines. Only ever asked for single-digit counts.
const romanNumeral = (n: number): string => {
  const map: [number, string][] = [[10, 'x'], [9, 'ix'], [5, 'v'], [4, 'iv'], [1, 'i']];
  let rest = Math.max(1, Math.round(n));
  let out = '';
  map.forEach(([v, sym]) => {
    while (rest >= v) { out += sym; rest -= v; }
  });
  return out;
};

const loadImageEl = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    const t = setTimeout(() => reject(new Error('timeout')), 10000);
    img.onload = () => { clearTimeout(t); resolve(img); };
    img.onerror = () => { clearTimeout(t); reject(new Error('failed')); };
    img.src = url;
  });

/** The agent's portal photo, clipped to a circle. False when there isn't one. */
const loadUserPhoto = async (
  doc: jsPDF,
  agent: AgentInfo,
  photoX: number,
  photoY: number,
  radius: number,
): Promise<boolean> => {
  if (!agent.Photo) return false;
  try {
    const photoPath = agent.Photo.startsWith('/') ? agent.Photo.substring(1) : agent.Photo;
    const photoUrl = `https://portal.huttonsgroup.com/${photoPath}`;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    return await new Promise<boolean>((resolve) => {
      const timeout = setTimeout(() => resolve(false), 5000);
      img.onload = () => {
        clearTimeout(timeout);
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) { resolve(false); return; }
          const size = radius * 2;
          const px = Math.max(256, Math.round(size * 12));
          canvas.width = px;
          canvas.height = px;
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          const r = px / 2;
          const maxInCircle = r * Math.SQRT2;
          const scale = Math.min(maxInCircle / img.width, maxInCircle / img.height);
          const sw = Math.round(img.width * scale);
          const sh = Math.round(img.height * scale);
          ctx.beginPath();
          ctx.arc(r, r, r, 0, Math.PI * 2);
          ctx.closePath();
          ctx.clip();
          ctx.drawImage(img, (px - sw) / 2, (px - sh) / 2, sw, sh);
          doc.addImage(canvas.toDataURL('image/png'), 'PNG', photoX - radius, photoY - radius, size, size);
          resolve(true);
        } catch {
          resolve(false);
        }
      };
      img.onerror = () => { clearTimeout(timeout); resolve(false); };
      img.src = photoUrl;
    });
  } catch {
    return false;
  }
};

// ── Page furniture ────────────────────────────────────────────────────────────

const MARGIN = 12;

/** Header (pages 2+): slim navy bar. Page indicator stamped in a final pass. */
const addReportHeader = (doc: jsPDF, title: string) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setFillColor(...SUMMARY_BAR_NAVY_RGB);
  doc.rect(0, 0, pageWidth, 10, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text(title.toUpperCase(), MARGIN, 6.5);
  doc.setTextColor(0, 0, 0);
};

const addFooter = (doc: jsPDF, logoDataUrl?: string, logoAspect?: number, agentInfo?: AgentInfo) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const footerY = pageHeight - 15;
  doc.setFillColor(...FOOTER_BG_RGB);
  doc.rect(0, footerY, pageWidth, 15, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  if (logoDataUrl && logoAspect) {
    const logoH = 7;
    const logoW = logoH * logoAspect;
    doc.addImage(logoDataUrl, 'PNG', 10, footerY + (15 - logoH) / 2, logoW, logoH);
  } else {
    doc.text('Huttons', 10, footerY + 8);
  }
  if (agentInfo?.Name) {
    doc.setTextColor(...ORANGE_RGB);
    doc.setFont('helvetica', 'bold');
    doc.text(agentInfo.Name, pageWidth - 10, footerY + 5, { align: 'right' });
    if (agentInfo.CEANumber) {
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'normal');
      doc.text(`CEA: ${agentInfo.CEANumber}`, pageWidth - 10, footerY + 11, { align: 'right' });
    }
  } else {
    doc.setTextColor(200, 200, 200);
    doc.text('Decoupling Report', pageWidth - 10, footerY + 8, { align: 'right' });
  }
  // The standing caveat rides in the footer bar so every page carries it.
  doc.setTextColor(150, 158, 172);
  doc.setFontSize(6);
  doc.setFont('helvetica', 'italic');
  doc.text(
    'Estimates only - not financial, legal or tax advice. Confirm duties with IRAS and the CPF refund with the CPF Board.',
    pageWidth / 2,
    footerY + 5.5,
    { align: 'center' },
  );
  doc.setTextColor(0, 0, 0);
};

/** Final pass: the page number and "PAGE x / total", once the count is known. */
const stampPageNumbers = (doc: jsPDF) => {
  const total = doc.getNumberOfPages();
  for (let p = 1; p <= total; p += 1) {
    doc.setPage(p);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(255, 255, 255);
    doc.text(`${p}`, pageWidth / 2, pageHeight - 4, { align: 'center' });
    // Page 1 is the cover, which carries no report header to stamp.
    if (p > 1) {
      doc.setFontSize(7.5);
      doc.setTextColor(220, 225, 235);
      doc.text(`PAGE ${p} / ${total}`, pageWidth - MARGIN, 6.5, { align: 'right' });
    }
    doc.setTextColor(0, 0, 0);
  }
};

// ── Cover page ────────────────────────────────────────────────────────────────

const renderCoverPage = async (
  doc: jsPDF,
  data: DecouplingPdfData,
  preparedFor: string,
  logoDataUrl?: string,
  logoAspect?: number,
) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const agent = data.agentInfo;

  doc.setFillColor(...DARK_BG_RGB);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');
  doc.setFillColor(...ORANGE_RGB);
  doc.rect(0, 0, pageWidth, 8, 'F');

  // Landscape pages have ~87mm less height — tighten the vertical rhythm so the
  // full cover (logo -> title -> agent card -> client -> disclaimer) still fits.
  const compact = pageHeight < 250;

  let yPos = compact ? 24 : 28;
  if (logoDataUrl && logoAspect) {
    const logoH = 12;
    const logoW = logoH * logoAspect;
    doc.addImage(logoDataUrl, 'PNG', (pageWidth - logoW) / 2, yPos - logoH + 2, logoW, logoH);
  } else {
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('HUTTONS', pageWidth / 2, yPos, { align: 'center' });
  }
  yPos += compact ? 8 : 10;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(200, 200, 200);
  doc.text("SINGAPORE'S LARGEST PRIVATE REAL ESTATE AGENCY", pageWidth / 2, yPos, { align: 'center' });

  yPos += compact ? 8 : 10;
  doc.setFillColor(...ORANGE_RGB);
  doc.rect(pageWidth / 2 - 45, yPos, 90, 1.2, 'F');

  yPos += compact ? 18 : 22;
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('DECOUPLING REPORT', pageWidth / 2, yPos, { align: 'center' });
  yPos += compact ? 9 : 10;
  doc.setTextColor(...ORANGE_RGB);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(TAB_LABEL[data.state.tab], pageWidth / 2, yPos, { align: 'center' });

  // The two sides of the transfer, named, so the cover says whose deal this is.
  const seller = data.state.exitingName.trim();
  const buyer = data.state.remainingName.trim();
  if (seller && buyer) {
    yPos += compact ? 7 : 8;
    doc.setTextColor(200, 200, 200);
    doc.setFontSize(9.5);
    doc.setFont('helvetica', 'normal');
    doc.text(
      `${seller} sells ${data.state.exitingSharePct}% to ${buyer}`,
      pageWidth / 2,
      yPos,
      { align: 'center' },
    );
  }
  yPos += compact ? 11 : 14;

  if (agent) {
    const infoBoxW = 120;
    const infoBoxH = compact ? 46 : 50;
    const infoBoxX = (pageWidth - infoBoxW) / 2;
    const infoBoxY = yPos;

    doc.setFillColor(...INFO_BOX_BG_RGB);
    doc.setDrawColor(...ORANGE_RGB);
    doc.setLineWidth(2);
    doc.roundedRect(infoBoxX, infoBoxY, infoBoxW, infoBoxH, 3, 3, 'FD');

    const photoRadius = 18;
    const photoPadding = 10;
    const photoX = infoBoxX + photoPadding + photoRadius;
    const photoY = infoBoxY + infoBoxH / 2;

    const photoLoaded = await loadUserPhoto(doc, agent, photoX, photoY, photoRadius);
    if (!photoLoaded) {
      doc.setFillColor(...ORANGE_RGB);
      doc.circle(photoX, photoY, photoRadius, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      const initials = agent.Name
        ? agent.Name.split(' ').map((n) => n[0]).join('').substring(0, 2).toUpperCase()
        : 'U';
      doc.text(initials, photoX, photoY + 3, { align: 'center' });
    }

    const textX = infoBoxX + photoPadding + photoRadius * 2 + 15;
    let detailY = infoBoxY + 12;
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    if (agent.Name) { doc.text(agent.Name, textX, detailY); detailY += 7; }
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    if (agent.CEANumber) {
      const label = 'CEA Registration: ';
      doc.text(label, textX, detailY);
      doc.setTextColor(...ORANGE_RGB);
      doc.setFont('helvetica', 'bold');
      doc.text(agent.CEANumber, textX + doc.getTextWidth(label), detailY);
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'normal');
      detailY += 6;
    }
    if (agent.Email) { doc.text(agent.Email, textX, detailY); detailY += 6; }
    if (agent.Mobile) { doc.text(agent.Mobile, textX, detailY); }

    yPos = infoBoxY + infoBoxH + (compact ? 10 : 14);
  } else {
    yPos += 8;
  }

  if (preparedFor) {
    yPos += compact ? 8 : 10;
    doc.setTextColor(200, 200, 200);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Specially Prepared for', pageWidth / 2, yPos, { align: 'center' });
    yPos += 9;
    doc.setTextColor(...ORANGE_RGB);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(preparedFor, pageWidth / 2, yPos, { align: 'center' });
    yPos += compact ? 10 : 12;
  }

  const genDate = new Date().toLocaleDateString('en-SG', { year: 'numeric', month: 'long', day: 'numeric' });
  doc.setTextColor(200, 200, 200);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generated on: ${genDate}`, pageWidth / 2, yPos, { align: 'center' });

  yPos += compact ? 8 : 10;
  doc.setFillColor(...ORANGE_RGB);
  doc.rect(pageWidth / 2 - 40, yPos, 80, 0.8, 'F');

  yPos += compact ? 8 : 10;
  doc.setTextColor(160, 160, 160);
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'italic');
  const disclaimer = 'This report is for informational purposes only. Duties, CPF refunds and loan terms may vary. Subject to MAS regulations, IRAS stamp duty rules, CPF Board rules and lender policies.';
  doc.text(doc.splitTextToSize(disclaimer, pageWidth - 80), pageWidth / 2, yPos, { align: 'center' });

  addFooter(doc, logoDataUrl, logoAspect, agent);
};

// ── Legal / tax-risk notes ────────────────────────────────────────────────────

/** Type and padding for a guard box. The poster squeezes three across the sheet;
 *  a page of their own can afford to set them at reading size. */
interface GuardType { title: number; body: number; pad: number }
const GUARD_TYPE_INLINE: GuardType = { title: 6.4, body: 4.9, pad: 3.4 };
const GUARD_TYPE_PAGE: GuardType = { title: 9, body: 7, pad: 4.6 };
const guardTitleLineMm = (t: GuardType) => (t.title / 2.8346) * 1.2;
const guardBodyLineMm = (t: GuardType) => (t.body / 2.8346) * 1.24;

interface GuardBox {
  guard: DecouplingGuard;
  titleLines: string[];
  bodyLines: string[];
  type: GuardType;
}

const guardBoxHeight = (b: GuardBox): number =>
  b.type.pad * 2
  + b.titleLines.length * guardTitleLineMm(b.type)
  + 1.4
  + b.bodyLines.length * guardBodyLineMm(b.type);

/** Measures the guards at a given box width. Heights come back with the box, so the
 *  caller can decide between the poster's spare strip and a page of its own. */
const measureGuards = (
  doc: jsPDF,
  guards: DecouplingGuard[],
  boxW: number,
  type: GuardType = GUARD_TYPE_INLINE,
): { boxes: GuardBox[]; height: number } => {
  const boxes = guards.map((guard) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(type.title);
    const titleLines = doc.splitTextToSize(guard.title, boxW - type.pad * 2 - 2) as string[];
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(type.body);
    const bodyLines = doc.splitTextToSize(guard.body.join(' '), boxW - type.pad * 2 - 2) as string[];
    return { guard, titleLines, bodyLines, type };
  });
  const height = boxes.reduce((tallest, b) => Math.max(tallest, guardBoxHeight(b)), 0);
  return { boxes, height };
};

const drawGuardBoxes = (doc: jsPDF, boxes: GuardBox[], x: number, y: number, boxW: number, boxH: number, gap: number) => {
  boxes.forEach((b, idx) => {
    const bx = x + idx * (boxW + gap);
    const { type } = b;
    const accent = b.guard.level === 'advisory' ? ORANGE : ALERT_RED_RGB;
    doc.setFillColor(250, 250, 251);
    doc.setDrawColor(...LIGHT_LINE);
    doc.setLineWidth(0.1);
    doc.roundedRect(bx, y, boxW, boxH, 1.4, 1.4, 'FD');
    doc.setFillColor(...accent);
    doc.rect(bx, y + 0.5, 1.5, boxH - 1, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(type.title);
    doc.setTextColor(...accent);
    doc.text(b.titleLines, bx + type.pad, y + type.pad + guardTitleLineMm(type) * 0.6);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(type.body);
    doc.setTextColor(95, 97, 104);
    doc.text(
      b.bodyLines,
      bx + type.pad,
      y + type.pad + guardTitleLineMm(type) * 0.6 + b.titleLines.length * guardTitleLineMm(type) + 1.4,
    );
    doc.setTextColor(0, 0, 0);
  });
};

// ── Poster page: cost, financing and exit position, on one landscape sheet ────

const renderDecouplingPage = (
  doc: jsPDF,
  data: DecouplingPdfData,
  logoDataUrl?: string,
  logoAspect?: number,
): void => {
  const { state, costs, financing, exitPosition, guards } = data;
  const cur = formatCurrency;
  const seller = state.exitingName.trim() || 'Seller';
  const buyer = state.remainingName.trim() || 'Buyer';
  const sellerShort = shortName(seller);
  const buyerShort = shortName(buyer);
  const ltvLabel = `${Math.round(financing.ltvPct * 100)}%`;

  doc.addPage('a4', 'landscape');
  addReportHeader(doc, 'Decoupling Report');
  addFooter(doc, logoDataUrl, logoAspect, data.agentInfo);

  // Reset stroke width — the cover page's photo circle leaves it at 2mm.
  doc.setLineWidth(0.2);

  const pageWidth = doc.internal.pageSize.getWidth();    // ~297
  const pageHeight = doc.internal.pageSize.getHeight();  // ~210
  const M = 10;
  const innerW = pageWidth - M * 2;

  // ── Title block ──
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...ORANGE);
  doc.text('ASSET PROGRESSION & RESTRUCTURING', M, 14);
  doc.setFontSize(16);
  doc.setTextColor(...NAVY);
  doc.text('Estimated Decoupling Cost & Financing', M, 21);
  doc.setFillColor(...ORANGE);
  doc.rect(M, 23.5, 36, 0.9, 'F');
  doc.setFillColor(...GOLD);
  doc.rect(M + 37, 23.7, 12, 0.55, 'F');

  // ── KPI cards row ──
  const kpiY = 26, kpiH = 17, kpiGap = 4;
  const kpiW = (innerW - kpiGap * 3) / 4;
  const kpiCard = (
    idx: number, label: string, value: string, sub: string,
    variant: 'navy' | 'accent' | 'plain',
    valueRed = false,
  ) => {
    const x = M + idx * (kpiW + kpiGap);
    if (variant === 'navy') {
      doc.setFillColor(...NAVY);
      doc.roundedRect(x, kpiY, kpiW, kpiH, 1.5, 1.5, 'F');
    } else {
      doc.setDrawColor(...LIGHT_LINE);
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(x, kpiY, kpiW, kpiH, 1.5, 1.5, 'FD');
      if (variant === 'accent') {
        doc.setFillColor(...ORANGE);
        doc.rect(x, kpiY, kpiW, 1.4, 'F');
      }
    }
    const valueColor: [number, number, number] = valueRed
      ? ALERT_RED_RGB
      : variant === 'navy' ? [255, 255, 255] : variant === 'accent' ? ORANGE : NAVY;
    const captionColor: [number, number, number] = variant === 'navy' ? [190, 198, 212] : GREY;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(5.6);
    doc.setTextColor(...(variant === 'navy' ? GOLD : GREY));
    doc.text(label, x + 4, kpiY + 5.4);
    doc.setFontSize(11.5);
    doc.setTextColor(...valueColor);
    doc.text(value, x + 4, kpiY + 11.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...captionColor);
    // The captions carry client names and loan terms, either of which can run past
    // the card edge — step the caption down until it fits.
    let subSize = 5.6;
    doc.setFontSize(subSize);
    while (doc.getTextWidth(sub) > kpiW - 8 && subSize > 4.2) {
      subSize -= 0.1;
      doc.setFontSize(subSize);
    }
    doc.text(sub, x + 4, kpiY + 15);
  };

  const negativeExit = exitPosition.netCash < 0;
  kpiCard(0, 'SHARE TRANSFERRED', cur(costs.shareValue),
    `${seller} ${state.exitingSharePct}% to ${buyer}`, 'navy');
  kpiCard(1, 'COST TO DECOUPLE', cur(costs.totalCost), 'Duties and fees, both sides', 'plain');
  kpiCard(2, 'NEW LOAN REQUIRED', cur(financing.requiredLoan),
    `${ltvLabel} LTV · ${financing.effectiveTenure} yrs @ ${state.bankRate.toFixed(2)}% p.a.`, 'accent');
  kpiCard(3, 'SELLER NET CASH', signedCurrency(exitPosition.netCash),
    negativeExit
      ? 'Top-up required to clear their loan share'
      : `Plus ${cur(exitPosition.cpfRefund)} back to CPF OA`,
    'plain', negativeExit);

  // ── Section header helper (numbered navy badge) ──
  const sectionHead = (n: number, title: string, sub: string, x: number, y: number) => {
    const r = 2.8;
    doc.setFillColor(...NAVY);
    doc.circle(x + r, y, r, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.3);
    doc.setTextColor(255, 255, 255);
    doc.text(String(n), x + r, y + 1.1, { align: 'center' });
    doc.setFontSize(9.7);
    doc.setTextColor(...NAVY);
    doc.text(title, x + r * 2 + 3, y + 1.35);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(5.6);
    doc.setTextColor(...GREY);
    doc.text(sub, x + r * 2 + 3, y + 5);
  };
  const HEAD_H = 6.5;

  // ── Section 1: the sequence of the transfer ──
  const s1Y = kpiY + kpiH + 6;
  sectionHead(1, 'Decoupling Timeline', 'How the transfer runs, and what falls due at each step.', M, s1Y);

  // Only two steps carry a real date: the completion the agent entered, and the
  // 14-day stamp duty deadline that IRAS sets from the signed transfer. The rest
  // show what is due rather than inventing a date for it.
  const stampDutyTotal = costs.bsd + costs.absd;
  const holding = state.ssdApplies ? holdingPeriodLabel(state.purchaseDate, state.transferDate) : '';
  type Card = {
    title: string;
    date?: string;
    amountLabel?: string;
    amount?: number;
    note: string;
    highlight?: boolean;
  };
  const cards: Card[] = [
    {
      title: 'VALUATION & ELIGIBILITY',
      amountLabel: 'MARKET VALUE',
      amount: state.marketValue,
      note: state.ssdApplies && state.purchaseDate
        ? `Bought ${displayDate(state.purchaseDate)}${holding ? ` - held ${holding}` : ''}. Sets the SSD band.`
        : 'Agree the valuation with the bank before the papers are drawn.',
    },
    {
      title: 'TRANSFER DOCUMENTS',
      amountLabel: 'SHARE VALUE',
      amount: costs.shareValue,
      note: `${seller} transfers ${state.exitingSharePct}% to ${buyer}. Each side instructs its own law firm.`,
    },
    {
      title: 'NEW SOLE-NAME LOAN',
      amountLabel: 'LOAN REQUIRED',
      amount: financing.requiredLoan,
      note: `${buyer} is re-assessed alone. Bank will lend up to ${cur(financing.maxLoan)}.`,
    },
    {
      title: 'STAMP DUTY',
      date: 'Within 14 days',
      amountLabel: 'BSD + ABSD',
      amount: stampDutyTotal,
      // With no ABSD and no SSD the split would just echo the headline figure -
      // name who pays it instead.
      note: costs.absd > 0 || costs.ssd > 0
        ? `BSD ${cur(costs.bsd)}${costs.absd > 0 ? ` · ABSD ${cur(costs.absd)}` : ''}${costs.ssd > 0 ? ` · Seller's SSD ${cur(costs.ssd)}` : ''}.`
        : `Buyer's Stamp Duty, payable by ${buyer} on the share acquired.`,
    },
    {
      title: 'COMPLETION',
      date: state.transferDate ? `Est. ${displayDate(state.transferDate)}` : undefined,
      amountLabel: 'BUYER CASH NEEDED',
      amount: financing.cashRequired,
      note: `Joint loan redeemed${exitPosition.cpfRefund > 0 ? `, ${cur(exitPosition.cpfRefund)} back to ${seller}'s CPF OA` : ''}. ${buyer} holds 100%.`,
      highlight: true,
    },
  ];

  const cardsTop = s1Y + HEAD_H;
  const cardGap = 2.5;
  const cardW = (innerW - cardGap * (cards.length - 1)) / cards.length;
  const cardH = 27;
  const cardHeaderH = 8.4;
  cards.forEach((c, idx) => {
    const highlight = c.highlight === true;
    const x = M + idx * (cardW + cardGap);
    if (highlight) {
      doc.setFillColor(...PEACH);
      doc.setDrawColor(...ORANGE);
    } else {
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(...LIGHT_LINE);
    }
    doc.setLineWidth(0.2);
    doc.roundedRect(x, cardsTop, cardW, cardH, 1.4, 1.4, 'FD');

    // Header band behind the milestone title, matching the on-screen timeline cards.
    doc.setFillColor(...(highlight ? PEACH : CARD_HEADER));
    doc.roundedRect(x, cardsTop, cardW, cardHeaderH, 1.4, 1.4, 'F');
    doc.rect(x, cardsTop + cardHeaderH - 1.4, cardW, 1.4, 'F');
    doc.setDrawColor(...(highlight ? ORANGE : LIGHT_LINE));
    doc.setLineWidth(0.1);
    doc.line(x, cardsTop + cardHeaderH, x + cardW, cardsTop + cardHeaderH);

    // number badge
    const br = 2.5;
    const bcx = x + 3.4 + br;
    doc.setFillColor(...(highlight ? ORANGE : NAVY));
    doc.circle(bcx, cardsTop + cardHeaderH / 2, br, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(5.4);
    doc.setTextColor(255, 255, 255);
    // Roman numerals are lowercase, so they sit lower than digits — a smaller
    // baseline offset keeps the glyph optically centred in the badge.
    doc.text(romanNumeral(idx + 1), bcx, cardsTop + cardHeaderH / 2 + 0.6, { align: 'center' });
    // title — clamp to the header band (2 lines max)
    doc.setFontSize(5.4);
    doc.setTextColor(...NAVY);
    const titleLines = (doc.splitTextToSize(c.title, cardW - 11) as string[]).slice(0, 2);
    doc.text(titleLines, bcx + br + 1.5, cardsTop + (titleLines.length > 1 ? 3.3 : 4.5));
    // Date line, where one is actually known. Without it the amount block moves up
    // into its place rather than leaving a hole.
    if (c.date) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.2);
      doc.setTextColor(...NAVY);
      doc.text(c.date, x + 3.4, cardsTop + 12.1);
    }
    if (c.amount != null && c.amountLabel) {
      const labelY = cardsTop + (c.date ? 15.3 : 12.6);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(4.6);
      doc.setTextColor(...GREY);
      doc.text(c.amountLabel, x + 3.4, labelY);
      doc.setFontSize(8.6);
      doc.setTextColor(...(highlight ? ORANGE : NAVY));
      doc.text(cur(c.amount), x + 3.4, labelY + 4.1);
    }
    // hairline
    doc.setDrawColor(...(highlight ? [242, 214, 190] as [number, number, number] : LIGHT_LINE));
    doc.setLineWidth(0.15);
    doc.line(x + 3.4, cardsTop + 20.4, x + cardW - 3.4, cardsTop + 20.4);
    // note — clamp so a long name never overflows the card
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(4.0);
    doc.setTextColor(110, 112, 118);
    const noteLines = (doc.splitTextToSize(c.note, cardW - 6.5) as string[]).slice(0, 3);
    doc.text(noteLines, x + 3.4, cardsTop + 22.9);
  });

  // ── Verdict band ──
  // Where the buying and selling posters run phase bands under their cards, the
  // decoupling question is a yes/no one: can the remaining owner hold the property
  // on their own income? That verdict takes the band instead.
  //
  // Everything downstream derives from the market value, so until one is entered a
  // zero required loan against a zero ceiling would read as a real verdict either
  // way. Say so rather than printing a green pass or a "shortfall of $0".
  const loanAssessable = state.marketValue > 0;
  const verdictY = cardsTop + cardH + 1.8;
  const verdictH = 9.4;
  const passes = financing.passes;
  doc.setFillColor(...(!loanAssessable
    ? SUMMARY_BAR_NAVY_RGB
    : passes ? SUCCESS_GREEN_RGB : ALERT_RED_RGB));
  doc.rect(M, verdictY, innerW, verdictH, 'F');
  doc.setFillColor(...GOLD);
  doc.rect(M, verdictY, 1.6, verdictH, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text(
    !loanAssessable
      ? 'FINANCING NOT YET ASSESSED'
      : passes
        ? `${buyer.toUpperCase()} CAN CARRY THE PROPERTY ALONE`
        : `SHORTFALL OF ${cur(financing.shortfall)}`,
    M + 5.5,
    verdictY + verdictH / 2 + 1.3,
  );
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.6);
  const verdictDetail = !loanAssessable
    ? "Enter the property's current market value to test the remaining owner's loan."
    : passes
      ? `Bank will lend up to ${cur(financing.maxLoan)} against ${cur(financing.requiredLoan)} needed  ·  Repayment ${cur(financing.monthlyAtBankRate)}/mth at ${state.bankRate.toFixed(2)}%`
      : `${cur(financing.requiredLoan)} needed, ${cur(financing.maxLoan)} available  ·  Close the gap with cash, CPF, a longer tenure or pledged funds`;
  doc.text(verdictDetail, M + innerW - 5, verdictY + verdictH / 2 + 1.2, { align: 'right' });
  doc.setTextColor(0, 0, 0);

  // ── Lower band: four columns, one figure set each ──
  const colGap = 5;
  const colW = (innerW - colGap * 3) / 4;
  const colX = (idx: number) => M + idx * (colW + colGap);
  const headY = verdictY + verdictH + 5.6;
  const tableY = headY + HEAD_H;
  const BOTTOM = pageHeight - 15 - 3;

  const groupRow = (label: string): RowInput => [{
    content: label,
    colSpan: 2,
    styles: {
      fillColor: CARD_HEADER,
      textColor: NAVY,
      fontStyle: 'bold' as const,
      halign: 'left' as const,
    },
  }];

  interface Block {
    n: number;
    title: string;
    sub: string;
    body: RowInput[];
    foot: RowInput[];
    footRed?: boolean;
    /** Body row indices carrying a running total — navy bold, so they lead the column. */
    strongRows?: number[];
    /** Body row indices whose figure is a real negative — printed red. */
    alertRows?: number[];
  }

  // ── ② The transfer itself ──
  const transferBody: RowInput[] = [
    ['Current market value', cur(state.marketValue)],
    ['Outstanding loan (joint)', cur(state.outstandingLoan)],
    ...(state.ssdApplies ? [['Purchase date', displayDate(state.purchaseDate)] as RowInput] : []),
    ['Transfer completion', displayDate(state.transferDate)],
    [`${sellerShort} transfers`, `${state.exitingSharePct}%`],
    [`${buyerShort} ends up with`, '100%'],
  ];

  // ── ③ What the decoupling costs, both sides ──
  const ssdLabel = `Seller's Stamp Duty${
    !state.ssdApplies
      ? ' (n/a)'
      : costs.ssdDetail
        // The full rate label ("0% (held 3 years or more, no SSD)") is a sentence —
        // only the rate itself fits the column.
        ? ` (${costs.ssdDetail.rateLabel.split(' ')[0]})`
        : ''
  }`;
  const costBody: RowInput[] = [
    groupRow(`Paid by ${buyerShort} (buyer)`),
    ["Buyer's Stamp Duty", cur(costs.bsd)],
    [`ABSD (${costs.absdRatePct}%)`, cur(costs.absd)],
    ['Legal, bank & admin fees', cur(costs.remainingOwnerFees)],
    ['Buyer subtotal', cur(costs.remainingOwnerTotal)],
    groupRow(`Paid by ${sellerShort} (seller)`),
    [ssdLabel, cur(costs.ssd)],
    ['Seller conveyancing', cur(costs.exitingOwnerFees)],
    ['Seller subtotal', cur(costs.exitingOwnerTotal)],
  ];

  // ── ④ The buyer's new sole-name loan ──
  // Own funds meet the downpayment first, so only the surplus shrinks the loan —
  // showing the whole sum here would read as a deduction the total never took.
  const ownFundsSurplus = financing.ownFundsApplied - financing.buyoutDownpayment;
  const loanBody: RowInput[] = [
    ["Buy over the seller's share", cur(financing.buyoutAmount)],
    [`Financed at ${ltvLabel} LTV`, cur(financing.buyoutFinanced)],
    [`Loan share redeemed (${state.remainingSharePct}%)`, cur(financing.ownLoanShare)],
    ...(ownFundsSurplus > 0
      ? [['Less own cash & CPF', `- ${cur(ownFundsSurplus)}`] as RowInput]
      : []),
    ['New loan required', cur(financing.requiredLoan)],
    [`LTV ceiling (${ltvLabel} of value)`, cur(financing.ltvCap)],
    [`Income ceiling (${financing.msrBinds ? 'MSR 30%' : 'TDSR 55%'})`, cur(financing.incomeCap)],
    ['Bank will lend', cur(financing.maxLoan)],
    ['Assessable monthly income', cur(financing.assessableIncome)],
    ['Monthly repayment capacity', cur(financing.bindingMonthlyCapacity)],
    [`Repayment at ${state.bankRate.toFixed(2)}%`, cur(financing.monthlyAtBankRate)],
    [`At the ${state.stressTestRate}% stress floor`, cur(financing.monthlyAtStressRate)],
    ['Loan tenure', `${financing.effectiveTenure} yrs`],
  ];
  const strongLoanRows = [
    loanBody.findIndex((r) => Array.isArray(r) && r[0] === 'New loan required'),
    loanBody.findIndex((r) => Array.isArray(r) && r[0] === 'Bank will lend'),
  ].filter((i) => i >= 0);

  // ── ⑤ What the seller walks away with ──
  const exitBody: RowInput[] = [
    ['Consideration for their share', cur(exitPosition.consideration)],
    ['Less CPF refund to own OA', `- ${cur(exitPosition.cpfRefund)}`],
    [`Less loan share (${state.exitingSharePct}%)`, `- ${cur(exitPosition.loanShareRedeemed)}`],
    ["Less Seller's Stamp Duty", `- ${cur(exitPosition.ssd)}`],
    ['Less seller conveyancing', `- ${cur(exitPosition.fees)}`],
    ['Net cash in hand', signedCurrency(exitPosition.netCash)],
    ['CPF OA restored (not cash)', cur(exitPosition.cpfRefund)],
  ];

  const blocks: Block[] = [
    {
      n: 2,
      title: 'The Transfer',
      sub: 'What moves, and on what basis.',
      body: transferBody,
      foot: [['Value of the share sold', cur(costs.shareValue)]],
    },
    {
      n: 3,
      title: 'Cost to Decouple',
      sub: 'Duties and fees, split by who pays them.',
      body: costBody,
      foot: [['Total cost to decouple', cur(costs.totalCost)]],
      strongRows: [4, 8],
    },
    {
      n: 4,
      title: `${buyerShort} - New Loan`,
      sub: 'Assessed on the buyer alone.',
      body: loanBody,
      foot: [['Cash on completion', cur(financing.cashRequired)]],
      strongRows: strongLoanRows,
    },
    {
      n: 5,
      title: `${sellerShort} - Exit Position`,
      sub: 'What the seller walks away with.',
      body: exitBody,
      foot: [['Total across both pockets', signedCurrency(exitPosition.totalReceived)]],
      footRed: exitPosition.totalReceived < 0,
      strongRows: [5],
      // A negative net cash is a real outcome on a geared property — flag it.
      alertRows: exitPosition.netCash < 0 ? [5] : [],
    },
  ];

  // Notes claim the foot of the page when they fit there; otherwise they take a
  // sheet of their own rather than being clipped — they are the legal warnings.
  const guardGap = 5;
  const guardBoxW = guards.length > 0
    ? (innerW - guardGap * (guards.length - 1)) / guards.length
    : innerW;
  const measured = guards.length > 0 ? measureGuards(doc, guards, guardBoxW) : null;
  const guardBandH = measured ? HEAD_H + measured.height : 0;
  const tableBand = BOTTOM - tableY;
  const guardsInline = measured != null && tableBand - guardBandH - 5 >= 46;
  const tableBottom = guardsInline ? BOTTOM - guardBandH - 5 : BOTTOM;

  // One type size across all four columns, stepped down until the tallest clears the
  // footer and every label clears its own column on one line. Row height follows
  // autoTable's own metrics: text height = fontSize / 2.8346 x 1.15, plus padding
  // and the hairline.
  const maxRows = Math.max(...blocks.map((b) => b.body.length + b.foot.length));
  const bodyLabels = blocks.flatMap((b) => b.body.map((r) => (Array.isArray(r) ? String(r[0] ?? '') : '')));
  const footLabels = blocks.flatMap((b) => b.foot.map((r) => (Array.isArray(r) ? String(r[0] ?? '') : '')));
  const lineMm = (f: number) => (f * 1.15) / 2.8346;
  const CELL_PAD_MIN = 0.95;
  const CELL_PAD_MAX = 1.9;
  const LABEL_PAD = 2.5;
  const labelW = colW * 0.6;
  const band = tableBottom - tableY;
  // Foot rows are bold and a notch larger, so they are measured as they print.
  const labelsFit = (f: number): boolean => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(f);
    if (!bodyLabels.every((l) => doc.getTextWidth(l) <= labelW - LABEL_PAD * 2)) return false;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(f + 0.2);
    return footLabels.every((l) => doc.getTextWidth(l) <= labelW - LABEL_PAD * 2);
  };
  let font = 8.4;
  while (font > 5.0
    && (maxRows * (lineMm(font) + CELL_PAD_MIN * 2 + 0.1) > band || !labelsFit(font))) {
    font -= 0.1;
  }
  // Whatever height the type left over goes back into the rows, so the columns reach
  // the footer instead of stranding a third of the sheet under the shortest one.
  const cellPad = Math.min(
    CELL_PAD_MAX,
    Math.max(CELL_PAD_MIN, (band / maxRows - lineMm(font) - 0.1) / 2),
  );

  const lastY = () => (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? tableY;

  const cellHook = (block: Block): CellHook => (h) => {
    if (h.section === 'foot' && h.column.index === 0) h.cell.styles.halign = 'left';
    const isAlert = h.section === 'body'
      && block.alertRows?.includes(h.row.index) === true
      && h.column.index === 1;
    // Deductions read as money leaving the pocket, so they take the orange the
    // buying and selling reports use for the same rows.
    if (h.section === 'body' && !isAlert
      && typeof h.cell.raw === 'string' && h.cell.raw.trim().startsWith('-')) {
      h.cell.styles.textColor = ORANGE;
    }
    if (h.section === 'body' && block.strongRows?.includes(h.row.index)) {
      h.cell.styles.fontStyle = 'bold';
      h.cell.styles.textColor = NAVY;
    }
    if (isAlert) h.cell.styles.textColor = ALERT_RED_RGB;
  };

  blocks.forEach((block, idx) => {
    const x = colX(idx);
    sectionHead(block.n, block.title, block.sub, x, headY);
    autoTable(doc, {
      startY: tableY,
      margin: { left: x, bottom: 15 },
      tableWidth: colW,
      theme: 'grid',
      styles: {
        font: 'helvetica',
        fontSize: font,
        cellPadding: { top: cellPad, bottom: cellPad, left: LABEL_PAD, right: LABEL_PAD },
        lineColor: LIGHT_LINE,
        lineWidth: 0.1,
        overflow: 'linebreak',
      },
      columnStyles: {
        0: { textColor: [60, 62, 70], halign: 'left', cellWidth: labelW },
        1: { halign: 'right', fontStyle: 'bold', textColor: NAVY },
      },
      body: block.body,
      foot: block.foot,
      footStyles: {
        fillColor: PEACH,
        textColor: block.footRed ? ALERT_RED_RGB : ORANGE,
        fontStyle: 'bold',
        halign: 'right',
        fontSize: font + 0.2,
      },
      didParseCell: cellHook(block),
    });
  });

  const columnsBottom = lastY();

  // ── ⑥ Points to check ──
  if (measured && guards.length > 0) {
    if (guardsInline) {
      const gy = Math.max(columnsBottom + 5, BOTTOM - guardBandH);
      sectionHead(6, 'Points To Check', 'Legal and tax risks flagged for this transfer.', M, gy);
      drawGuardBoxes(doc, measured.boxes, M, gy + HEAD_H, guardBoxW, measured.height, guardGap);
    } else {
      doc.addPage('a4', 'landscape');
      addReportHeader(doc, 'Decoupling Report');
      addFooter(doc, logoDataUrl, logoAspect, data.agentInfo);
      doc.setLineWidth(0.2);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.setTextColor(...ORANGE);
      doc.text('ASSET PROGRESSION & RESTRUCTURING', M, 14);
      doc.setFontSize(16);
      doc.setTextColor(...NAVY);
      doc.text('Points To Check Before You Decouple', M, 21);
      doc.setFillColor(...ORANGE);
      doc.rect(M, 23.5, 36, 0.9, 'F');
      doc.setFillColor(...GOLD);
      doc.rect(M + 37, 23.7, 12, 0.55, 'F');

      // Full width, one under the other — these read as paragraphs, not as cards.
      const full = measureGuards(doc, guards, innerW, GUARD_TYPE_PAGE);
      let gy = 30;
      full.boxes.forEach((b) => {
        const h = guardBoxHeight(b);
        drawGuardBoxes(doc, [b], M, gy, innerW, h, 0);
        gy += h + 5;
      });
    }
  }
};

// ── Filename ──────────────────────────────────────────────────────────────────

const sanitizeFilePart = (name?: string): string =>
  (name ?? '').trim().replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim();

const buildExportFilename = (data: DecouplingPdfData): string => {
  const who = sanitizeFilePart(data.profileName) || [data.state.exitingName, data.state.remainingName]
    .map((n) => n.trim())
    .filter((n) => n && n !== 'Seller' && n !== 'Buyer')
    .join(' & ');
  const ts = new Date().toLocaleString('en-SG', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  }).replace(/:/g, '.');
  return `${[who, 'Decoupling Calculator', ts].filter(Boolean).join(' - ')}.pdf`;
};

// ── Public API ────────────────────────────────────────────────────────────────

export const exportDecouplingToPDF = async (
  data: DecouplingPdfData,
  filename?: string,
): Promise<void> => {
  // Landscape throughout — matches the buying and selling reports.
  // compress: the Huttons logo is a 4210x1035 PNG that jsPDF would otherwise embed as
  // raw RGBA, turning a small report into a ~17 MB file.
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4', compress: true });

  let logoDataUrl: string | undefined;
  let logoAspect: number | undefined;
  try {
    const logoImg = await loadImageEl(HuttonsWhiteLogo);
    logoDataUrl = HuttonsWhiteLogo;
    logoAspect = logoImg.width / logoImg.height;
  } catch { /* fall back to the wordmark */ }

  const { state } = data;
  // The report names both sides for what they are: the exiting owner sells their share,
  // the remaining owner buys it. (The state keys stay `exiting*` / `remaining*` — they
  // are persisted in saved history snapshots.)
  const preparedFor = data.profileName?.trim()
    || [state.exitingName, state.remainingName]
      .map((n) => n.trim())
      .filter((n) => n && n !== 'Seller' && n !== 'Buyer')
      .join(' & ');

  await renderCoverPage(doc, data, preparedFor, logoDataUrl, logoAspect);
  renderDecouplingPage(doc, data, logoDataUrl, logoAspect);

  stampPageNumbers(doc);

  await savePdfDocument(doc, filename ?? buildExportFilename(data));
};
